import { z } from "zod";
/**
 * Multi-device dashboard layout: the persisted view spec for a compact
 * multi-device dashboard (block order + shared overlay-plot config). Two
 * variants by owner:
 *
 * - PROCESS (harvest_operation_template_revision.dashboard_layout): references
 *   device ids — a run that reuses the same devices inherits the arrangement;
 *   unrecognised ids fall back to member order. Per-device pins are NOT stored
 *   here yet (template-level pin authoring is deferred).
 * - QUICK (quick_rig.layout): references device ids directly (the rig membership
 *   IS the instance) and carries per-device pins.
 *
 * Both are MUTABLE-IN-PLACE display state, never copy-on-write. The DB column
 * defaults to '{}' (the "no layout — derive a default" sentinel).
 *
 * The frontend works in a single device-id-space `ResolvedLayout`; `resolve*`
 * merges a stored layout with the rig render output, and `serialize*` turns an
 * edited ResolvedLayout back into the stored variant for persistence.
 */
export const axisSide = z.enum(["left", "right"]);
// ── stored: process variant ──────────────────────────────────────────────────
/** Overlay series referenced by device id. */
export const processOverlaySeries = z.object({
    deviceId: z.number().int(),
    channelKey: z.string().min(1),
    axisSide: axisSide.optional(),
});
export const processDashboardLayout = z.object({
    kind: z.literal("process"),
    version: z.literal(1).default(1),
    blockOrder: z.array(z.number().int()).default([]), // ordered device ids
    overlay: z.array(processOverlaySeries).default([]),
});
// ── stored: quick variant ────────────────────────────────────────────────────
export const quickOverlaySeries = z.object({
    deviceId: z.number().int(),
    channelKey: z.string().min(1),
    axisSide: axisSide.optional(),
});
export const quickDashboardLayout = z.object({
    kind: z.literal("quick"),
    version: z.literal(1).default(1),
    blockOrder: z.array(z.number().int()).default([]), // ordered device ids
    overlay: z.array(quickOverlaySeries).default([]),
    pins: z
        .array(z.object({
        deviceId: z.number().int(),
        channelKeys: z.array(z.string().min(1)),
    }))
        .default([]),
});
export const dashboardLayout = z.discriminatedUnion("kind", [
    processDashboardLayout,
    quickDashboardLayout,
]);
/** The '{}' DB sentinel parses as the empty object; callers derive a default. */
export const dashboardLayoutOrEmpty = z.union([
    dashboardLayout,
    z.object({}).strict(),
]);
/** Parse a raw JSONB value into a layout, or null when it's the empty sentinel. */
export function parseStoredLayout(raw) {
    const parsed = dashboardLayoutOrEmpty.safeParse(raw);
    if (!parsed.success)
        return null;
    return "kind" in parsed.data ? parsed.data : null;
}
/**
 * Process resolution: block order by stored device-id order (then any unlisted
 * members in member order); overlay device-keyed directly, series for non-member
 * devices dropped. Per-device pins are not authored at the template level yet.
 */
export function resolveProcessLayout(stored, members) {
    const byDeviceId = new Map(members.map((m) => [m.deviceId, m]));
    const orderedIds = [];
    const seen = new Set();
    for (const id of stored?.blockOrder ?? []) {
        if (byDeviceId.has(id) && !seen.has(id)) {
            orderedIds.push(id);
            seen.add(id);
        }
    }
    for (const m of members) {
        if (!seen.has(m.deviceId)) {
            orderedIds.push(m.deviceId);
            seen.add(m.deviceId);
        }
    }
    const blocks = orderedIds.map((deviceId) => ({
        deviceId,
        pinnedChannelKeys: [],
    }));
    const overlay = (stored?.overlay ?? [])
        .filter((s) => byDeviceId.has(s.deviceId))
        .map((s) => ({
        deviceId: s.deviceId,
        channelKey: s.channelKey,
        axisSide: s.axisSide,
    }));
    return { blocks, overlay };
}
/**
 * Quick resolution: block order by stored device-id order (then any unlisted
 * members); pins from the stored per-device map; overlay device-keyed directly,
 * series for non-member devices dropped.
 */
export function resolveQuickLayout(stored, members) {
    const byDeviceId = new Map(members.map((m) => [m.deviceId, m]));
    const pinsByDevice = new Map((stored?.pins ?? []).map((p) => [p.deviceId, p.channelKeys]));
    const orderedIds = [];
    const seen = new Set();
    for (const id of stored?.blockOrder ?? []) {
        if (byDeviceId.has(id) && !seen.has(id)) {
            orderedIds.push(id);
            seen.add(id);
        }
    }
    for (const m of members) {
        if (!seen.has(m.deviceId)) {
            orderedIds.push(m.deviceId);
            seen.add(m.deviceId);
        }
    }
    const blocks = orderedIds.map((deviceId) => ({
        deviceId,
        pinnedChannelKeys: pinsByDevice.get(deviceId) ?? [],
    }));
    const overlay = (stored?.overlay ?? [])
        .filter((s) => byDeviceId.has(s.deviceId))
        .map((s) => ({
        deviceId: s.deviceId,
        channelKey: s.channelKey,
        axisSide: s.axisSide,
    }));
    return { blocks, overlay };
}
// ── serialize: edited ResolvedLayout → stored variant for persistence ────────
/** Process: device ids straight through; overlay device-keyed. */
export function toProcessLayout(resolved, _members) {
    return {
        kind: "process",
        version: 1,
        blockOrder: resolved.blocks.map((b) => b.deviceId),
        overlay: resolved.overlay.map((s) => ({
            deviceId: s.deviceId,
            channelKey: s.channelKey,
            axisSide: s.axisSide,
        })),
    };
}
/** Quick: device ids straight through; pins kept per device. */
export function toQuickLayout(resolved) {
    return {
        kind: "quick",
        version: 1,
        blockOrder: resolved.blocks.map((b) => b.deviceId),
        overlay: resolved.overlay.map((s) => ({
            deviceId: s.deviceId,
            channelKey: s.channelKey,
            axisSide: s.axisSide,
        })),
        pins: resolved.blocks
            .filter((b) => b.pinnedChannelKeys.length > 0)
            .map((b) => ({ deviceId: b.deviceId, channelKeys: b.pinnedChannelKeys })),
    };
}
//# sourceMappingURL=dashboard-layout.js.map